import {
  useCustomerReducedQuery,
  useVehiclesFilterReduced,
} from '@/core/domain/people/Customers/Customers.hooks';
import { Alert, Button, Form, Popover } from 'antd';
import { useEffect, useState } from 'react';
import { Info } from 'react-feather';
import Group from '../../Group';
import { LinkOutlined } from '@ant-design/icons';
import {
  ChangeVehicleOwnershipRequestInterface,
  VehicleFormData,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { useChangeVehicleOwnership } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useDebounce } from '@/hooks';

interface Props {
  licensePlate?: string;
  customerId?: number;
  onChange?: (alertStatus?: AlertStatus) => void;
  onChangeVehicle?: () => void;
}

export enum AlertStatus {
  Created = 'Created',
  Inactive = 'Inactive',
  Other = 'Other',
}

interface CustomerData {
  id: number;
  name: string;
  vehicleId?: number;
}

interface SetCustomers {
  activeOwner?: CustomerData;
  currentOwner?: CustomerData;
  alertStatus: AlertStatus;
}

const VehicleAlert: React.FC<Props> = ({
  licensePlate: currentLicensePlate,
  customerId,
  onChange,
  onChangeVehicle,
}) => {
  const form = Form.useFormInstance<VehicleFormData>();
  const licensePlateWatch = Form.useWatch('licensePlate', form);
  const customerIdWatch = Form.useWatch('customerId', form);
  const vehicleIdWatch = Form.useWatch('vehicleId', form);

  const licensePlateWatchDebounce = useDebounce(licensePlateWatch);

  const activeCustomerData = useCustomerReducedQuery(customerIdWatch);

  const activeVehiclesFilterReduced = useVehiclesFilterReduced(
    {
      licensePlate: licensePlateWatchDebounce,
      rows: 1,
      active: true,
    },
    !!licensePlateWatchDebounce,
  );
  const activeVehicle = flatInfiniteData(activeVehiclesFilterReduced.data).at(
    0,
  );

  const activeVehicleCustomer = useCustomerReducedQuery(
    activeVehicle?.customerId,
  );

  const { data: currentCustomer } = useCustomerReducedQuery(customerId);

  const currentVehiclesFilterReduced = useVehiclesFilterReduced(
    { customerId, licensePlate: licensePlateWatchDebounce, rows: 1 },
    !!licensePlateWatchDebounce && !!customerId,
  );
  const currentVehicle = flatInfiniteData(currentVehiclesFilterReduced.data).at(
    0,
  );

  const changeVehicleOwner = useChangeVehicleOwnership(customerId);
  const activeVehicleOwner = useChangeVehicleOwnership(customerId);

  const [alertStatus, setAlertStatus] = useState<AlertStatus>();
  const [activeCustomer, setActiveCustomer] = useState<CustomerData>();

  useEffect(() => {
    if (
      !!licensePlateWatchDebounce &&
      currentLicensePlate !== licensePlateWatchDebounce
    ) {
      if (
        currentVehiclesFilterReduced.isLoading ||
        activeVehiclesFilterReduced.isLoading ||
        activeVehicleCustomer.isLoading
      )
        return;

      setAlertStatus(undefined);

      const activeOwner = activeCustomerData.data;

      if (currentCustomer && currentVehicle) {
        if (currentVehicle.active) {
          if (
            !currentLicensePlate ||
            currentLicensePlate !== currentVehicle.licensePlate
          ) {
            setCustomers({ alertStatus: AlertStatus.Created });
          }
        } else if (activeOwner) {
          setCustomers({
            alertStatus: AlertStatus.Inactive,
            activeOwner: {
              id: activeOwner.id,
              name: activeOwner.name,
              vehicleId: vehicleIdWatch,
            },
          });
        } else {
          setCustomers({
            alertStatus: AlertStatus.Inactive,
            activeOwner: currentCustomer
              ? {
                  id: currentCustomer.id,
                  name: currentCustomer.name,
                  vehicleId: currentVehicle?.id,
                }
              : undefined,
          });
        }
      } else if (activeOwner) {
        setCustomers({
          alertStatus: AlertStatus.Other,
          activeOwner: {
            id: activeOwner.id,
            name: activeOwner.name,
            vehicleId: vehicleIdWatch,
          },
        });
      } else {
        setCustomers(undefined);
      }
    }
  }, [
    currentVehiclesFilterReduced.isLoading,
    activeVehiclesFilterReduced.isLoading,
    activeVehicleCustomer.isLoading,
    currentCustomer,
    !!licensePlateWatchDebounce,
    currentVehicle,
    customerIdWatch,
    vehicleIdWatch,
    activeCustomerData.isLoading,
  ]);

  function setCustomers(params?: SetCustomers) {
    setAlertStatus(params?.alertStatus);

    if (onChange) {
      onChange(params?.alertStatus);
    }

    if (params?.activeOwner) {
      setActiveCustomer(params?.activeOwner);
    }
  }

  function viewUser(id: number) {
    return (
      <Button
        icon={<LinkOutlined style={{ marginRight: -12 }} />}
        onClick={() => window.open(`/app/customers/${id}/general`)}
        type="link"
      >
        {activeCustomer?.name?.split(' ')[0]}
      </Button>
    );
  }

  async function handleChangeVehicleOwner() {
    if (customerId && activeCustomer?.vehicleId) {
      const obj: ChangeVehicleOwnershipRequestInterface = {
        idNewCustomer: customerId,
        newVehicleId: currentVehicle?.id,
        idOldCustomer: activeCustomer.id,
        oldVehicleId: activeCustomer.vehicleId,
      };

      await changeVehicleOwner.mutateAsync(obj);

      !!onChangeVehicle && onChangeVehicle();
    }
  }

  async function handleActiveVehicleOwner() {
    if (customerId && activeCustomer?.vehicleId && currentVehicle) {
      const obj: ChangeVehicleOwnershipRequestInterface = {
        idNewCustomer: customerId,
        idOldCustomer: activeCustomer.id,
        oldVehicleId: activeCustomer.vehicleId,
        newVehicleId: currentVehicle.id,
      };

      await activeVehicleOwner.mutateAsync(obj);

      !!onChangeVehicle && onChangeVehicle();
    }
  }

  if (!!alertStatus && currentLicensePlate !== licensePlateWatchDebounce)
    return (
      <>
        <Alert
          type="warning"
          message={
            <Group gap={10}>
              <b>Atenção</b>
              <Popover
                trigger="click"
                content="Alterar ou reativar veículo não influenciará no histórico de veículo"
              >
                <Info size={16} style={{ cursor: 'pointer' }} />
              </Popover>
            </Group>
          }
          description={
            <>
              {alertStatus === AlertStatus.Inactive &&
                `Este veículo já foi cadastrado anteriormente para este cliente, gostaria de reativar o mesmo veículo? Esta ação irá inativar o veículo de 
              "${activeCustomer?.name || '-'}".`}

              {alertStatus === AlertStatus.Other &&
                `Já existe um veículo com a placa ${licensePlateWatchDebounce} no cadastro de "${
                  activeCustomer?.name || '-'
                }", deseja passar o veículo para ${
                  currentCustomer?.name
                    ? `"${currentCustomer?.name || '-'}"`
                    : 'este cliente'
                }?`}

              {alertStatus === AlertStatus.Created &&
                `Este veículo já está cadastrado para "${
                  currentCustomer?.name || '-'
                }".`}

              {alertStatus === AlertStatus.Inactive && (
                <Group gap={24}>
                  <Button
                    type="link"
                    onClick={handleActiveVehicleOwner}
                    loading={activeVehicleOwner.isLoading}
                  >
                    Reativar veículo
                  </Button>

                  {!!customerId && viewUser(customerId)}
                </Group>
              )}

              {alertStatus === AlertStatus.Other && (
                <Group gap={24}>
                  <Button
                    type="link"
                    onClick={handleChangeVehicleOwner}
                    loading={changeVehicleOwner.isLoading}
                  >
                    Alterar proprietários
                  </Button>

                  {!!activeCustomer?.id && viewUser(activeCustomer.id)}
                </Group>
              )}
            </>
          }
          style={{ marginBottom: 24 }}
        />
      </>
    );

  return null;
};

export default VehicleAlert;
